import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08; // brief white flinch flash on taking damage

/**
 * A slow minion with no ranged attack that detonates a damaging AoE burst
 * around itself the instant its HP hits 0 — `minion:exploded` is emitted
 * alongside the normal `minion:died` (see Minion.js) and handled by
 * Game.js (the only place that reliably has the live player reference at
 * the moment of death, since takeDamage() itself is called from
 * CombatSystem's collision loop, not from something with `player` on hand).
 */
export class Bomber {
  constructor(eventBus, {
    x, y, radius = 7, hp, moveSpeed, contactDamage,
    explodeDamage = 20, explodeRadius = 30,
  }) {
    this.eventBus = eventBus;
    this.type = 'bomber';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.explodeDamage = explodeDamage;
    this.explodeRadius = explodeRadius;
    this.isDead = false;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
  }

  update(dt, player, _bulletPool) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;

    this._clampToArena();

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  // Defense-in-depth — see Skirmisher._clampToArena / Charger's charge-duration cap.
  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    // Emit the clamped HP delta, not the raw incoming hit — see
    // Boss.takeDamage's identical fix, same overkill-XP issue here on a
    // one-shot kill.
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
      this.eventBus.emit('minion:exploded', { x: this.x, y: this.y, damage: this.explodeDamage, radius: this.explodeRadius });
    }
  }
}
